<template>
  <div>
    <v-card class="mt-5 pa-4" :loading="loading">
      <div v-if="hasReport">
        <p class="text-h6 mb-4">Ежемесячный отчет по всем проектам (поминутка)</p>

        <div class="d-flex flex-wrap mb-4">
          <v-badge
            bordered
            color="info"
            overlap
            class="mr-4"
          >
            <v-chip color="info" outlined small>Проектов</v-chip>
            <template #badge>
              {{ formatNumber(stats.count_projects) }}
            </template>
          </v-badge>

          <v-badge
            bordered
            color="error"
            overlap
            class="mr-12"
          >
            <v-chip color="error" outlined small>Сумма потенц. пот.</v-chip>
            <template #badge>
              {{ formatNumber(stats.sum_potential_lost_money) }}
            </template>
          </v-badge>

          <v-badge
            bordered
            color="warning"
            overlap
          >
            <v-chip color="warning" outlined small>Средний % принятых</v-chip>
            <template #badge>
              {{ formatPercent(stats.avg_accepted_percent) }}
            </template>
          </v-badge>
        </div>

        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th>Проект</th>
                <th class="sortable" @click="toggleSort('accepted_percent')">
                  % принятых звонков
                  <v-icon small :class="sortIconClass('accepted_percent')">{{ sortIcon('accepted_percent') }}</v-icon>
                </th>
                <th class="sortable" @click="toggleSort('spoken_minutes')">
                  Выговор минут
                  <v-icon small :class="sortIconClass('spoken_minutes')">{{ sortIcon('spoken_minutes') }}</v-icon>
                </th>
                <th class="sortable" @click="toggleSort('avg_dialog_minutes')">
                  Сред. длит. звонка
                  <v-icon small :class="sortIconClass('avg_dialog_minutes')">{{ sortIcon('avg_dialog_minutes') }}</v-icon>
                </th>
                <th class="sortable" @click="toggleSort('package_deviation')">
                  Отклонение от пакета
                  <v-icon small :class="sortIconClass('package_deviation')">{{ sortIcon('package_deviation') }}</v-icon>
                </th>
                <th class="sortable" @click="toggleSort('lost_minutes')">
                  Не принято минут (при 90%)
                  <v-icon small :class="sortIconClass('lost_minutes')">{{ sortIcon('lost_minutes') }}</v-icon>
                </th>
                <th class="sortable" @click="toggleSort('potential_lost_money')">
                  Потенциально потеряно денег при 90%
                  <v-icon small :class="sortIconClass('potential_lost_money')">{{ sortIcon('potential_lost_money') }}</v-icon>
                </th>
                <th>Статус риска</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in sortedRows" :key="index">
                <td>{{ row.project }}</td>
                <td class="font-weight-bold">{{ formatPercent(row.accepted_percent) }}</td>
                <td>{{ formatNumber(row.spoken_minutes) }}</td>
                <td>{{ formatMinutes(row.avg_dialog_minutes) }}</td>
                <td :class="deviationClass(row.package_deviation)">
                  {{ formatDeviation(row.package_deviation) }}
                </td>
                <td>{{ formatNumber(row.lost_minutes) }}</td>
                <td>{{ formatMoney(row.potential_lost_money) }}</td>
                <td :class="riskStatusClass(row.accepted_percent)">
                  {{ riskStatus(row.accepted_percent) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card>

    <v-card v-if="hasReport" class="mt-5 pa-4" :loading="loading">
      <p class="text-h6 mb-1">Диаграмма для управленческого решения</p>
      <p class="text-caption grey--text mb-4">
        Комбинированная диаграмма: столбцы — потенциально потеряно денег, линия — % принятых.
        Красная пунктирная линия — норматив 90%.
      </p>


      <apexchart
        type="line"
        height="350"
        :options="chartOptions"
        :series="chartSeries"
      />

    </v-card>
  </div>
</template>

<script>
const NORM_PERCENT = 90

export default {
  name: 'MonthlyReport',
  props: {
    report: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortKey: null,
      sortOrder: 'desc',
      chartOptions: {
        chart: {
          type: 'line',
          toolbar: { show: false },
          fontFamily: 'inherit',
          background: '#00000000',
          foreColor: '#373d3f',
        },
        theme: {
          mode: 'light',
        },
        colors: ['#3B82F6', '#22C55E'],
        stroke: {
          width: [0, 3],
          curve: 'straight',
        },
        plotOptions: {
          bar: {
            columnWidth: '45%',
            borderRadius: 6,
            borderRadiusApplication: 'end',
          },
        },
        markers: {
          size: [0, 5],
          colors: ['#22C55E'],
          strokeColors: '#fff',
          strokeWidth: 2,
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '13px',
            },
          },
          axisBorder: {
            show: true,
            color: '#9CA3AF',
          },
          axisTicks: { show: false },
        },
        yaxis: [
          {
            seriesName: 'Потенциально потеряно денег',
            min: 0,
            labels: {
              formatter: (value) => Number(value).toLocaleString('ru-RU'),
            },
          },
          {
            opposite: true,
            seriesName: '% принятых',
            min: 0,
            max: 100,
            tickAmount: 5,
            labels: {
              formatter: (value) => `${Math.round(value)}%`,
            },
          },
        ],
        annotations: {
          yaxis: [
            {
              y: NORM_PERCENT,
              yAxisIndex: 1,
              borderColor: '#EF4444',
              strokeDashArray: 6,
              label: {
                text: 'норматив 90%',
                position: 'right',
                offsetX: 0,
                borderColor: 'transparent',
                style: {
                  color: '#EF4444',
                  background: 'transparent',
                  fontSize: '12px',
                  fontWeight: 600,
                },
              },
            },
          ],
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${Number(value).toLocaleString('ru-RU')} руб.`
              }
              return `${Number(value).toLocaleString('ru-RU')}%`
            },
          },
        },
      },
    }
  },
  computed: {
    isDarkTheme() {
      return this.$vuetify.theme.isDark
    },
    hasReport() {
      return Array.isArray(this.report?.rows) && this.report.rows.length > 0
    },
    stats() {
      return this.report?.general_statistics || {}
    },
    rows() {
      return this.report?.rows || []
    },
    sortedRows() {
      if (!this.sortKey) return this.rows

      const key = this.sortKey
      const order = this.sortOrder === 'asc' ? 1 : -1

      return [...this.rows].sort((a, b) => {
        const valueA = Number(a[key]) || 0
        const valueB = Number(b[key]) || 0
        return (valueA - valueB) * order
      })
    },
    chartSeries() {
      return [
        {
          name: 'Потенциально потеряно денег',
          type: 'column',
          data: this.sortedRows.map((row) => Number(row.potential_lost_money) || 0),
        },
        {
          name: '% принятых',
          type: 'line',
          data: this.sortedRows.map((row) => Number(row.accepted_percent) || 0),
        },
      ]
    },
  },
  watch: {
    isDarkTheme() {
      this.changeApexTheme()
    },
    sortedRows: {
      immediate: true,
      handler(rows) {
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: {
            ...this.chartOptions.xaxis,
            categories: rows.map((row) => row.project),
          },
        }
      },
    },
  },
  methods: {
    changeApexTheme() {
      if (this.isDarkTheme) {
        this.chartOptions = {
          ...this.chartOptions,
          chart: {
            ...this.chartOptions.chart,
            background: '#00000000',
            foreColor: '#f6f7f8',
          },
          theme: {
            mode: 'dark',
          },
          grid: {
            ...this.chartOptions.grid,
            borderColor: '#424242',
          },
        }
      } else {
        this.chartOptions = {
          ...this.chartOptions,
          chart: {
            ...this.chartOptions.chart,
            background: '#00000000',
            foreColor: '#373d3f',
          },
          theme: {
            mode: 'light',
          },
          grid: {
            ...this.chartOptions.grid,
            borderColor: '#E5E7EB',
          },
        }
      }
    },
    formatNumber(value) {
      if (value == null || value === '') return '—'
      return Number(value).toLocaleString('ru-RU')
    },
    formatPercent(value) {
      if (value == null || value === '') return '—'
      return `${Number(value).toLocaleString('ru-RU')}%`
    },
    formatMoney(value) {
      if (value == null || value === '') return '—'
      return `${Number(value).toLocaleString('ru-RU')} руб.`
    },
    formatDeviation(value) {
      if (value == null || value === '') return '—'
      const num = Number(value)
      const formatted = Math.abs(num).toLocaleString('ru-RU')
      if (num > 0) return `+${formatted}`
      if (num < 0) return `−${formatted}`
      return formatted
    },
    deviationClass(value) {
      const num = Number(value)
      if (num > 0) return 'deviation-positive'
      if (num < 0) return 'deviation-negative'
      return ''
    },
    formatMinutes(value) {
      if (value == null || value === '') return '—'
      return `${Number(value).toLocaleString('ru-RU')} мин.`
    },
    riskStatus(acceptedPercent) {
      return Number(acceptedPercent) < NORM_PERCENT ? 'Ниже 90%' : 'Норма'
    },
    riskStatusClass(acceptedPercent) {
      return Number(acceptedPercent) < NORM_PERCENT ? 'risk-below' : 'risk-ok'
    },
    toggleSort(key) {
      if (this.sortKey !== key) {
        this.sortKey = key
        this.sortOrder = 'desc'
        return
      }

      if (this.sortOrder === 'desc') {
        this.sortOrder = 'asc'
      } else {
        this.sortKey = null
        this.sortOrder = 'desc'
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'mdi-unfold-more-horizontal'
      return this.sortOrder === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up'
    },
    sortIconClass(key) {
      return this.sortKey === key ? 'sort-icon-active' : 'sort-icon'
    },
  },
  mounted() {
    this.changeApexTheme()
  },
}
</script>

<style scoped lang="scss">
.deviation-positive {
  color: #2e7d32;
  font-weight: 600;
}

.deviation-negative {
  color: #e65100;
  font-weight: 600;
}

.risk-below {
  color: #c62828;
  font-weight: 600;
}

.risk-ok {
  color: #2e7d32;
  font-weight: 600;
}

.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sort-icon {
  opacity: 0.4;
}

.sort-icon-active {
  opacity: 1;
}

.chart-info-box {
  background: #f3f4f6;
  border-radius: 12px;
  padding: 20px 24px;
  height: 100%;
}

.chart-info-list {
  margin: 0;
  padding-left: 18px;
  color: #374151;
  line-height: 1.6;

  li + li {
    margin-top: 8px;
  }
}
</style>
